import { useEffect, useRef, useState } from 'react';
import storeService from '../../services/store.service';
import {
  EMPTY_STORE_SETUP,
  SETUP_INCLUDED,
  StoreSetupDraft,
  StoreSetupIncluded
} from '../../types/storeSetup.types';
import {
  SetupBeat,
  setupChunks,
  setupIds,
  setupItemLabel,
  setupStepDelay,
  wait
} from './setupDraft';

export type SetupMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
};

const OPENING = 'Décrivez la boutique. Le setup se construit à côté.';

let messageSeq = 0;

function nextMessage(role: SetupMessage['role'], text: string): SetupMessage {
  messageSeq += 1;
  return { id: `m-${messageSeq}`, role, text };
}

type Options = {
  onStoreName: (name: string) => void;
};

export function useStoreSetupChat({ onStoreName }: Options) {
  const [setup, setSetup] = useState<StoreSetupDraft>(EMPTY_STORE_SETUP);
  const [shown, setShown] = useState<string[]>([]);
  const [beat, setBeat] = useState<SetupBeat | null>(null);
  const [messages, setMessages] = useState<SetupMessage[]>([nextMessage('assistant', OPENING)]);
  const [included, setIncluded] = useState<StoreSetupIncluded>(SETUP_INCLUDED);
  const setupRef = useRef(setup);
  const genRef = useRef(0);

  useEffect(() => {
    setupRef.current = setup;
  }, [setup]);

  useEffect(() => () => {
    genRef.current += 1;
  }, []);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const token = genRef.current + 1;
    genRef.current = token;
    const alive = () => genRef.current === token;

    setMessages((current) => [...current, nextMessage('user', trimmed)]);
    setBeat({ phase: 'think', label: 'Je prépare le setup', itemId: null });

    try {
      const response = await storeService.previewSetup(trimmed, setupRef.current);
      if (!alive()) return;
      const data = response.data;
      if (!response.success || !data?.setup) {
        throw new Error(response.error?.message || 'preview');
      }

      if (data.included?.fields?.length) setIncluded(data.included);
      if (data.storeName) onStoreName(data.storeName);

      const previous = new Set(setupIds(setupRef.current));
      const fresh = data.placed.filter((id) => !previous.has(id));
      const revisited = data.placed.filter((id) => previous.has(id));
      const immediate = setupIds(data.setup).filter((id) => !fresh.includes(id));

      setupRef.current = data.setup;
      setSetup(data.setup);
      setShown(immediate);
      setMessages((current) => [...current, nextMessage('assistant', data.message)]);

      if (!fresh.length && !revisited.length) {
        setBeat(null);
        return;
      }

      for (const chunk of setupChunks(data.setup, fresh)) {
        if (!alive()) return;
        setShown((current) => {
          const next = new Set(current);
          chunk.ids.forEach((id) => next.add(id));
          return setupIds(data.setup).filter((id) => next.has(id));
        });
        setBeat({
          phase: 'place',
          label: chunk.label,
          itemId: chunk.ids[chunk.ids.length - 1]
        });
        await wait(setupStepDelay());
      }
      const highlight = revisited[revisited.length - 1];
      if (highlight && alive()) {
        setBeat({ phase: 'place', label: setupItemLabel(data.setup, highlight), itemId: highlight });
        await wait(setupStepDelay());
      }
      if (!alive()) return;

      const closing = data.message.includes('?') ? data.message : 'Prêt';
      setBeat({ phase: 'done', label: closing, itemId: null });
      await wait(setupStepDelay());
      if (!alive()) return;
      setBeat(null);
    } catch {
      if (!alive()) return;
      setBeat(null);
      setMessages((current) => [
        ...current,
        nextMessage('assistant', 'Je n’arrive pas à préparer le setup. Réessayez.')
      ]);
    }
  };

  return {
    setup,
    shown,
    beat,
    messages,
    included,
    send
  };
}
